import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { useIntl } from '@grandvisionhq/state';
import { FormContext } from '../Form/Form.js';

function isCheckboxValue(value) {
    return typeof value === 'object' && 'checked' in value;
}
const getFieldValue = (value)=>{
    if (isCheckboxValue(value)) {
        return value.checked ? 'true' : '';
    }
    return value;
};
const validate = (value, validations)=>{
    if (!validations) {
        return true;
    }
    return validations.reduce((isValid, validator)=>validator(getFieldValue(value)) && isValid
    , true);
};
const DefaultError = /*#__PURE__*/ React.memo(({ message , id , className  })=>/*#__PURE__*/ React.createElement("p", {
        id: id,
        className: classnames('field__error', className)
    }, message)
);
DefaultError.displayName = 'Memo(DefaultError)';
/*
 * <Field /> should always be used within a <Form />
 */ const Field = (_param)=>{
    var { children , name , id , label , icon , required , disabled , placeholder , error , className , validations  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "name",
        "id",
        "label",
        "icon",
        "required",
        "disabled",
        "placeholder",
        "error",
        "className",
        "validations"
    ]);
    const resolvedId = id || name;
    const { getLabel  } = useIntl();
    const context = React.useContext(FormContext);
    const state = context.getInitialState && context.getInitialState(name) || {
        name,
        value: ''
    };
    const deps = [
        state.value,
        state.isValid,
        state.isTouched
    ];
    const handleChange = React.useCallback((value)=>{
        if (state.readonly) {
            return;
        }
        const updatedState = swcHelpers.objectSpread({}, state, {
            value: getFieldValue(value),
            isValid: validate(value, validations)
        });
        context.onChange && context.onChange(updatedState);
        rest.onChange && rest.onChange(updatedState);
    }, [
        ...deps,
        validations,
        context.onChange,
        rest.onChange
    ]);
    const handleBlur = React.useCallback((value)=>{
        if (state.readonly) {
            return;
        }
        const updatedState = swcHelpers.objectSpread({}, state, {
            value: getFieldValue(value),
            isTouched: true
        });
        context.onBlur && context.onBlur(updatedState);
        rest.onBlur && rest.onBlur(updatedState);
    }, [
        ...deps,
        context.onBlur,
        rest.onBlur
    ]);
    // To validate initial state value from context, on initial render and a change of validations
    React.useEffect(()=>{
        if (!context.onChange) {
            return;
        }
        const newValidationResult = validate(state.value, validations);
        // if the result of the validation of the value is the same as in context, skip update
        if (state.isValid !== newValidationResult) {
            context.onChange(swcHelpers.objectSpread({}, state, {
                isValid: newValidationResult
            }));
        }
    }, [
        validations,
        state.value,
        state.isValid,
        context.onChange,
        name
    ]);
    const { isValid , isTouched , readonly  } = state;
    const shouldDisplayValid = isValid === true && isTouched === true && state.value !== '' && !readonly;
    const shouldDisplayError = isValid === false && isTouched === true && !readonly;
    const childClasses = classnames(className, {
        _is_valid: shouldDisplayValid,
        _has_error: shouldDisplayError
    });
    const { showRequiredAndOptionalLabels  } = context;
    const displayRequiredLabel = showRequiredAndOptionalLabels && required;
    const displayOptionalLabel = showRequiredAndOptionalLabels && !required;
    const errorContent = React.useMemo(()=>typeof error === 'string' ? shouldDisplayError && error !== '' && /*#__PURE__*/ React.createElement(DefaultError, {
            id: `${resolvedId}_error`,
            message: error
        }) : error(shouldDisplayError)
    , [
        shouldDisplayError,
        resolvedId,
        error
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('field', className)
    }, label && /*#__PURE__*/ React.createElement("div", {
        className: "field__label-wrapper"
    }, /*#__PURE__*/ React.createElement("label", {
        htmlFor: resolvedId,
        className: "field__label"
    }, label, displayRequiredLabel && /*#__PURE__*/ React.createElement("span", {
        className: "field__required-asterisk"
    }, "*")), displayOptionalLabel && /*#__PURE__*/ React.createElement("small", {
        className: "field__optional-tag"
    }, getLabel('common.optional'))), /*#__PURE__*/ React.createElement("div", {
        className: "field__content"
    }, React.Children.map(children, (child)=>{
        return /*#__PURE__*/ React.cloneElement(child, {
            name,
            id: resolvedId,
            value: state.value,
            onChange: handleChange,
            onBlur: handleBlur,
            className: childClasses,
            required,
            disabled,
            placeholder
        });
    })), errorContent);
};

export { DefaultError, Field as default };
//# sourceMappingURL=Field.js.map
